import axios from './axiositeachWrapper';
import Config from '../_config/config';
import { IconKey } from '../constants/iconMap';
import portletConfig from '../portlet-config.json';

export interface PortletIcon {
  type: 'emoji' | 'image' | 'url' | 'lucide' | 'key';
  value?: string; // emoji char, image URL, or lucide icon name (kebab-case)
  key?: IconKey; // reference to ICON_MAP
  color?: string; // optional Lucide icon tint; omitted = inherit default
}

export interface PortletRoute {
  path: string;
}

export interface Portlet {
  id: string;
  title: string;
  description: string;
  icon: PortletIcon;
  route: PortletRoute;
  gradient?: string;
  visible: boolean;
  roleIds?: number[];
  order: number;
  category?: string;
}

export interface PortletConfig {
  portlets: Portlet[];
}

class PortletService {
  /**
   * Get portlets for the current user.
   * On fetch failure (nodefork down / 5xx) falls back to the last ACL the
   * backend granted this user (cached per userId). Never falls back to the
   * full default portlet list — that would expose everything on an outage.
   * Throws when there is no cache, so callers render an error state.
   */
  async getPortlets(): Promise<Portlet[]> {
    const userId = this.getCurrentUserId();
    try {
      console.log('Fetching portlets from API');
      const portlets = await this.fetchFromDatabase(userId);
      this.writeAclCache(userId, portlets);
      return portlets;
    } catch (error) {
      console.error('Error getting portlets:', error);
      const cached = this.readAclCache(userId);
      if (cached) {
        console.warn('Server unreachable — using last known portlet ACL for this user');
        return cached;
      }
      throw error;
    }
  }

  /**
   * Fetch portlet configuration from database (customer_detail_master.iTeachACL)
   */
  private async fetchFromDatabase(userId: number | null): Promise<Portlet[]> {
    try {
      const response = await axios.get<{ success: boolean; data: PortletConfig; message: string }>(
        `${Config.nodeSiteUrl}v1/portlets/user-acl?userId=${userId}`
      );

      if (response.data && response.data.success && response.data.data && response.data.data.portlets) {
        return this.enrichWithCategory(response.data.data.portlets);
      }

      // Backend reachable but tenant has no iTeachACL configured — defaults are legitimate here
      return this.getDefaultPortlets();
    } catch (error) {
      console.error('Error fetching portlets from database:', error);
      throw error;
    }
  }

  private getCurrentUserId(): number | null {
    try {
      const currentUserStr = localStorage.getItem('currentUser');
      const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
      return currentUser?.userId || null;
    } catch {
      return null;
    }
  }

  private aclCacheKey(userId: number | null): string {
    return `portletAcl:${userId ?? 'anon'}`;
  }

  private writeAclCache(userId: number | null, portlets: Portlet[]): void {
    try {
      localStorage.setItem(this.aclCacheKey(userId), JSON.stringify(portlets));
    } catch {
      // localStorage full/unavailable — cache is best-effort
    }
  }

  private readAclCache(userId: number | null): Portlet[] | null {
    try {
      const raw = localStorage.getItem(this.aclCacheKey(userId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * Force refresh portlets from database
   */
  async refreshPortlets(): Promise<Portlet[]> {
    return this.getPortlets();
  }

  /**
   * Filter portlets by visible status only (role filtering done on backend),
   * then collapse duplicate cards.
   *
   * The tenant ACL (customer_detail_master.iTeachACL) can carry several entries
   * that are the same app to the user — either the same id, or (more commonly)
   * distinct "link" portlets added twice via Access Control that share a title
   * and destination, e.g. two "Flows" → "/flows" cards with different ids. A
   * user whose roles match more than one sees the app several times (and React
   * hits duplicate keys). We key on title + destination (falling back to id
   * when there's no destination) and keep the lowest-order entry so the result
   * is stable and unique.
   */
  filterPortletsByRole(portlets: Portlet[]): Portlet[] {
    const sorted = portlets
      .filter(portlet => portlet.visible)
      .sort((a, b) => a.order - b.order);

    const seen = new Set<string>();
    return sorted.filter(portlet => {
      const key = this.portletDedupeKey(portlet);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /**
   * Uniqueness key for de-duplicating home-screen cards. Cards that open the
   * same destination under the same title are the same app to the user even
   * when their ids differ (link portlets added twice). Distinct titles or
   * distinct destinations stay separate; when there's no destination we fall
   * back to the id so unrelated cards aren't merged.
   */
  private portletDedupeKey(p: Portlet): string {
    const dest = (p.route?.path || (p as any).url || '').trim().toLowerCase();
    const title = (p.title || '').trim().toLowerCase();
    return dest ? `${title}|${dest}` : `id:${p.id}`;
  }

  /**
   * Enrich portlets with category from portlet-config.json when missing.
   * Handles the case where the DB iTeachACL JSON hasn't been updated with category yet.
   */
  private enrichWithCategory(portlets: Portlet[]): Portlet[] {
    const categoryMap = new Map<string, string>();
    for (const p of portletConfig.portlets as Portlet[]) {
      if (p.category) categoryMap.set(p.id, p.category);
    }
    return portlets.map(p => {
      if (p.category) return p;
      const cat = categoryMap.get(p.id);
      return cat ? { ...p, category: cat } : p;
    });
  }

  /**
   * Default portlets configuration (fallback)
   * Loads from portlet-config.json to maintain single source of truth
   */
  private getDefaultPortlets(): Portlet[] {
    return portletConfig.portlets as Portlet[];
  }
}

export default new PortletService();
